// src/pages/ClientByIDPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "../components/ui/LoadingComponent";
import Avatar from "react-avatar";

const ClientsDetailLayer = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiCall("get", `/get-client-by-id/${clientId}`, null, navigate);
      if (response?.data?.success) {
        setClient(response.data.data);
      } else {
        setError("Failed to load client details.");
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
      setError(error?.message || "Error fetching client details.");
    } finally {
      setLoading(false);
    }
  };

  const getClientLogo = (url) => {
    if (url && url.trim() !== "") {
      return `https://pevbackend.codovio.com/public/${url}`;
    }
    return null;
  };

  // Helper function to get a safe name for the Avatar
  const getSafeClientName = (client) => {
    if (client?.client_name && typeof client.client_name === 'string' && client.client_name.trim()) {
      return client.client_name.trim();
    }
    return "Client"; // Default fallback
  };

  useEffect(() => {
    fetchClientDetails();
  }, [clientId]);

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <div>
      <div className="card h-100 p-0 radius-12 overflow-hidden">
        <div className="card-body p-40">
          <h6 className="text-md  text-primary-light mb-16">Client Details - #{clientId}</h6>
          
          {error && (
            <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError("")}
              ></button>
            </div>
          )}

          {client ? (
            <form action="#" autoComplete="off">
              {/* Client Logo/Avatar Section */}
              <div className="text-center mb-32">
                {getClientLogo(client?.logo_url) ? (
                  <img
                    loading="lazy"
                    src={getClientLogo(client.logo_url)}
                    alt="Client Logo"
                    style={{ width: "100px", height: "100px", borderRadius: "50%" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                ) : null}
                <Avatar
                  name={getSafeClientName(client)}
                  size="100"
                  round={true}
                  style={{
                    display: getClientLogo(client?.logo_url) ? "none" : "block",
                  }}
                />
              </div>

              {/* Client Details Form */}
              <div className="row">
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Client ID
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.client_id || "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Client Name
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.client_name || "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Client Type
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.client_type || "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control radius-8"
                      value={client?.email || "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.phone_no || "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Plan Details
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.plan_name ? `${client.plan_name} (ID: ${client.plan_id})` : "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Trial Start Date
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={
                        client?.trial_start_date
                          ? new Date(client.trial_start_date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "N/A"
                      }
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Trial End Date
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={
                        client?.trial_end_date
                          ? new Date(client.trial_end_date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "N/A"
                      }
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Created By
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.created_by || "N/A"}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Created Date
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={
                        client?.created_datetime
                          ? new Date(client.created_datetime).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "N/A"
                      }
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Status
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={client?.is_active ? "Active" : "Inactive"}
                      readOnly
                      style={{ 
                        backgroundColor: "#f8f9fa", 
                        cursor: "not-allowed",
                        color: client?.is_active ? "#28a745" : "#dc3545",
                        fontWeight: "600"
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <button
                    type="button"
                className="btn btn-primary-600 btn-sm px-3 d-flex align-items-center"
                    onClick={() => navigate("/all-clients")}
                  >
                    Back to Clients
                  </button>
                  <button
                    type="button"
                className="btn btn-primary-600 btn-sm px-3 d-flex align-items-center"
                  >
                    Edit Client
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <p className="text-muted">No client data available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientsDetailLayer;